/**
 * StoryScene.ts — Main gameplay scene.
 *
 * Displays story nodes, presents choices, handles skill checks,
 * applies effects, manages chapter transitions, and auto-saves.
 *
 * Migrated to FrameRenderer + PanelWriter for framed UI layout.
 */

import type { GameEngine, Scene } from '@core/engine/GameEngine';
import type { MenuOption } from '@ui/MenuSystem';
import type { PanelWriter } from '@ui/PanelWriter';
import type { StoryNode, StoryChoice } from '@game/data/types';
import type { GameState } from '@core/engine/GameState';
import type { HintTriggerType } from '@ui/HintManager';
import { getClass } from '@game/data/classes';
import { theme } from '@ui/ColorTheme';
import { CharacterScene } from './CharacterScene';
import { CompletionScene } from './CompletionScene';
import { CombatScene } from './CombatScene';
import { ErrorScene } from './ErrorScene';
import { analytics } from '@game/Analytics';
import { getEnemy } from '@game/data/enemies';
import type { CombatResult } from '@core/combat/CombatTypes';

export class StoryScene implements Scene {
  name = 'StoryScene';

  private lastChapter = 0;
  private feedbackBuffer: Array<{ text: string; color: import('@ui/ColorTheme').ColorName }> = [];
  private resumeFromOverlay = false;

  async enter(engine: GameEngine): Promise<void> {
    const ctx = engine.getContext();

    if (!ctx.gameState) {
      throw new Error('StoryScene entered without a GameState');
    }

    // Track chapter for transition detection
    this.lastChapter = ctx.gameState.story.currentChapter;

    // Set up effect listeners
    const effectCleanup = this.setupEffectListeners(engine, ctx.gameState);

    try {
      await this.gameLoop(engine);
    } catch (err) {
      console.error('StoryScene error:', err);
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      await engine.replaceScene(new ErrorScene(message));
    } finally {
      effectCleanup();
    }
  }

  async exit(engine: GameEngine): Promise<void> {
    // Hide sprites on scene exit
    engine.getContext().sprites.hide();
  }

  // ─── Main Game Loop ──────────────────────────────────────

  private async gameLoop(engine: GameEngine): Promise<void> {
    const { storyEngine, gameState } = engine.getContext();

    if (!gameState) return;

    while (true) {
      const node = storyEngine.getCurrentNode();
      if (!node) {
        await engine.replaceScene(new CompletionScene());
        return;
      }

      // When resuming from Character Sheet overlay, skip re-displaying the
      // same node — jump straight to choices so the player isn't forced to
      // re-read narrative they've already seen.
      if (this.resumeFromOverlay) {
        this.resumeFromOverlay = false;

        // Update visuals for current location
        this.updateVisuals(engine, node);

        // Get available choices and go directly to choice presentation
        const choices = storyEngine.getFilteredChoices(node);
        if (choices.length === 0) {
          await engine.replaceScene(new CompletionScene());
          return;
        }
        const nodeText = storyEngine.getClassSpecificText(node);
        const contextText = nodeText ? nodeText.split('\n').slice(-3).join('\n') : undefined;
        const selectedChoice = await this.presentChoices(engine, choices, gameState, contextText);
        if (selectedChoice === null) {
          // Opened character sheet again
          continue;
        }
        await this.processChoice(engine, selectedChoice, gameState);
        engine.getContext().characterSystem.checkLevelUp(gameState);
        this.checkAbilityEvolutions(engine, gameState);
        this.checkAchievements(engine, gameState);
        const currentNode = storyEngine.getCurrentNode();
        if (currentNode?.isCheckpoint) {
          this.autoSave(engine, gameState, currentNode);
        }
        continue;
      }

      // Check for chapter transition
      if (node.chapter !== this.lastChapter && node.chapter > 0) {
        await this.showChapterTransition(engine, node);
        this.lastChapter = node.chapter;

        // Auto-save at chapter transitions (feedback shown in next frame via buffer)
        const { saveSystem } = engine.getContext();
        const cls = getClass(gameState.character.classId);
        try {
          const saveSuccess = saveSystem.save(0, gameState, node.title, cls.name);
          if (saveSuccess) {
            this.feedbackBuffer.push({ text: '  Progress saved', color: 'green' });
          } else {
            this.feedbackBuffer.push({ text: '  Save failed', color: 'yellow' });
          }
        } catch (err) {
          console.warn('[StoryScene] Auto-save failed:', err);
          this.feedbackBuffer.push({ text: '  Save failed', color: 'yellow' });
        }

        analytics.chapterProgress(node.chapter, node.act);
        this.checkAbilityEvolutions(engine, gameState);
      }

      // Check for combat encounter on this node
      if (node.combatEncounter) {
        const combatResult = await this.handleCombatEncounter(engine, node, gameState);
        if (combatResult) {
          const nextNodeId = combatResult.outcome === 'victory'
            ? node.combatEncounter.victoryNodeId
            : node.combatEncounter.defeatNodeId;
          storyEngine.advanceToNode(nextNodeId);
          continue;
        }
      }

      // Update visual layer particles based on location
      this.updateVisuals(engine, node);

      // Display the node
      const writer = await this.displayNode(engine, node, gameState);

      // Let the player read before showing choices (uses the same writer
      // so the prompt appears after the narrative text, not at row 3)
      await writer.pressAnyKey();

      // Get available choices
      const choices = storyEngine.getFilteredChoices(node);

      if (choices.length === 0) {
        await engine.replaceScene(new CompletionScene());
        return;
      }

      // Capture last 2-3 lines of narrative as context for choice screen
      const nodeText = storyEngine.getClassSpecificText(node);
      const contextText = nodeText ? nodeText.split('\n').slice(-3).join('\n') : undefined;

      // Present choices and get selection
      const selectedChoice = await this.presentChoices(engine, choices, gameState, contextText);

      if (selectedChoice === null) {
        // Player opened character sheet — set flag so re-entry skips node display
        this.resumeFromOverlay = true;
        continue;
      }

      // Process the selected choice
      await this.processChoice(engine, selectedChoice, gameState);

      // Check for pending level-ups (XP may have been awarded via story effects)
      engine.getContext().characterSystem.checkLevelUp(gameState);

      // Check ability evolutions after flag changes
      this.checkAbilityEvolutions(engine, gameState);

      // Check achievements after each choice
      this.checkAchievements(engine, gameState);

      // Auto-save at checkpoint nodes
      const currentNode = storyEngine.getCurrentNode();
      if (currentNode?.isCheckpoint) {
        this.autoSave(engine, gameState, currentNode);
      }
    }
  }

  // ─── Node Display ────────────────────────────────────────

  private async displayNode(engine: GameEngine, node: StoryNode, state: GameState): Promise<PanelWriter> {
    const ctx = engine.getContext();
    const { storyEngine, settings, frameRenderer, effects, terminalFrame, hintManager } = ctx;
    const speed = settings.getTypewriterSpeed();
    const cls = getClass(state.character.classId);

    // Update TerminalFrame info
    terminalFrame.updateInfo({
      heroName: state.character.name,
      className: cls.name,
      chapter: node.chapter ? `Chapter ${node.chapter}` : '',
      location: node.location ?? '',
    });

    // Build footer with HP/frostbite/corruption summary
    const footer = `HP: ${state.character.hp}/${state.character.hpMax}  |  Frost: ${state.character.frostbite}/100  |  Corruption: ${state.character.corruption}/100`;

    // Hide sprites before drawing frame
    ctx.sprites.hide();

    // Draw frame
    const layout = frameRenderer.drawFrame({
      title: node.chapter ? `Ch.${node.chapter}: ${node.title}` : node.title,
      headerRight: node.location ?? '',
      footer,
      borderColor: 'frostBlue',
    });

    // Show location sprite if available (viewport-aware height)
    if (node.location) {
      const spriteKey = this.getLocationSprite(node.location);
      if (spriteKey) {
        try {
          const spriteHeight = ctx.viewport.getSpriteHeight();
          await ctx.sprites.drawInPanel(spriteKey, layout.content.startRow, layout.content.startCol, layout.content.width, spriteHeight);
        } catch (err) {
          console.warn(`Failed to load sprite for location "${node.location}":`, err);
          // Continue without sprite - not a critical failure
        }
      }
    }

    const writer = frameRenderer.createWriter(layout.content, effects);

    // Show buffered feedback from previous choice effects (inside the frame)
    this.flushFeedback(writer);

    // Narrative text — use class-specific text if available, interpolate character name
    const rawText = storyEngine.getClassSpecificText(node);
    const text = rawText?.replace(/\{characterName\}/g, state.character.name);
    if (text) {
      if (speed > 0) {
        await writer.typewriterParagraph(text.split('\n'), 'white', speed);
      } else {
        for (const line of text.split('\n')) {
          writer.writeLine(line, 'white');
        }
      }
    }

    // Additional class-specific flavor
    if (node.classSpecificText) {
      const classText = node.classSpecificText[state.character.classId];
      if (classText && classText !== text) {
        writer.blankLine();
        if (speed > 0) {
          await writer.typewriterParagraph(classText.split('\n'), 'frostBlue', speed);
        } else {
          for (const line of classText.split('\n')) {
            writer.writeLine(line, 'frostBlue');
          }
        }
      }
    }

    writer.blankLine();

    // Trigger hints based on node context
    await this.triggerContextHints(hintManager, node, writer);

    return writer;
  }

  // ─── Choice Presentation ─────────────────────────────────

  private async presentChoices(
    engine: GameEngine,
    choices: StoryChoice[],
    state: GameState,
    contextText?: string,
  ): Promise<StoryChoice | null> {
    const ctx = engine.getContext();
    const { menu, skillCheck, statCheckRenderer, frameRenderer, terminalFrame, effects } = ctx;
    const cls = getClass(state.character.classId);

    const options: MenuOption[] = choices.map(choice => {
      const opt: MenuOption = {
        label: choice.text,
        color: this.getChoiceColor(choice),
      };

      if (choice.skillCheck) {
        const willPass = skillCheck.wouldPassCheck(choice, state);
        const gate = statCheckRenderer.formatGate(
          choice.skillCheck.stat,
          choice.skillCheck.difficulty,
          state.getEffectiveStat(choice.skillCheck.stat),
        );
        opt.suffix = `${gate.text} ${willPass ? '\u2713' : '\u2717'}`;
        opt.suffixColor = willPass ? 'green' : 'red';
      }

      if (choice.traitIndicator && !opt.suffix) {
        opt.suffix = `[${choice.traitIndicator}]`;
        opt.suffixColor = 'frostSilver';
      }

      return opt;
    });

    // Build footer
    const footer = `HP: ${state.character.hp}/${state.character.hpMax}  |  Frost: ${state.character.frostbite}/100  |  Corruption: ${state.character.corruption}/100`;
    const currentNode = ctx.storyEngine.getCurrentNode();

    // Update TerminalFrame
    terminalFrame.updateInfo({
      heroName: state.character.name,
      className: cls.name,
      chapter: currentNode?.chapter ? `Chapter ${currentNode.chapter}` : '',
      location: currentNode?.location ?? '',
    });

    // Hide sprites before redraw
    ctx.sprites.hide();

    // Draw frame for choice page with optional context preservation
    const drawChoiceFrame = () => {
      const layout = frameRenderer.drawFrame({
        title: currentNode?.chapter ? `Ch.${currentNode.chapter}: ${currentNode.title}` : currentNode?.title ?? '',
        headerRight: currentNode?.location ?? '',
        footer,
        borderColor: 'frostBlue',
      });

      // Preserve last 2-3 lines of narrative context above choices
      if (contextText) {
        const writer = frameRenderer.createWriter(layout.content, effects);
        const lines = contextText.split('\n').filter(l => l.trim().length > 0);
        for (const line of lines) {
          writer.writeLine(line, 'khaki');
        }
        writer.blankLine();
      }
    };

    drawChoiceFrame();

    const layout = frameRenderer.getLayout()!;
    const contentStartRow = contextText
      ? layout.content.startRow + contextText.split('\n').filter(l => l.trim().length > 0).length + 1
      : layout.content.startRow;

    // Check if auto-advance applies
    const continueWords = ['continue', 'proceed', 'onward', 'forward', 'next', 'move on'];
    const shouldAutoAdvance =
      ctx.settings.get().autoAdvanceContinue &&
      choices.length === 1 &&
      !choices[0].skillCheck &&
      !choices[0].requirements &&
      continueWords.some(word => choices[0].text.toLowerCase().includes(word));

    if (shouldAutoAdvance) {
      // Show single choice in muted color with countdown (frame-aware)
      const autoWriter = frameRenderer.createWriter(layout.content, effects);
      autoWriter.reset(contentStartRow - layout.content.startRow);
      autoWriter.writeLine(`  ${choices[0].text}`, 'khaki');
      autoWriter.blankLine();
      autoWriter.writeLine('  (auto-advancing... press any key to skip)', 'charcoal');

      // Wait 500ms or until keypress
      await new Promise<void>((resolve) => {
        let resolved = false;
        const previousMode = ctx.input.getMode();
        ctx.input.setMode('anyKey');

        const timeout = setTimeout(() => {
          if (!resolved) {
            resolved = true;
            ctx.input.off(handler);
            ctx.input.setMode(previousMode);
            resolve();
          }
        }, 500);

        const handler = () => {
          if (!resolved) {
            resolved = true;
            clearTimeout(timeout);
            ctx.input.off(handler);
            ctx.input.setMode(previousMode);
            resolve();
          }
        };

        ctx.input.on(handler);
      });

      return choices[0];
    }

    const menuCfg = {
      showExit: true,
      exitLabel: 'Character Sheet',
      startRow: contentStartRow,
      startCol: layout.content.startCol,
      maxWidth: layout.content.width,
      onBeforeRender: drawChoiceFrame,
    };

    const selection = await menu.show(options, menuCfg);

    if (selection === -1) {
      await engine.pushScene(new CharacterScene());
      return null;
    }

    return choices[selection];
  }

  private getChoiceColor(choice: StoryChoice): 'white' | 'green' | 'red' | 'magenta' | 'yellow' {
    switch (choice.style) {
      case 'success': return 'green';
      case 'failure': return 'red';
      case 'special': return 'magenta';
      case 'danger': return 'yellow';
      default: return 'white';
    }
  }

  // ─── Choice Processing ───────────────────────────────────

  private async processChoice(
    engine: GameEngine,
    choice: StoryChoice,
    state: GameState,
  ): Promise<void> {
    const { storyEngine, skillCheck, statCheckRenderer, settings } = engine.getContext();

    if (choice.skillCheck) {
      const { result, nextNodeId } = skillCheck.resolveCheck(choice.skillCheck, state);

      if (settings.get().statCheckAnimation) {
        await statCheckRenderer.render({
          statName: choice.skillCheck.stat.charAt(0).toUpperCase() + choice.skillCheck.stat.slice(1),
          threshold: choice.skillCheck.difficulty,
          playerValue: result.playerValue,
        });
      } else {
        statCheckRenderer.renderInstant({
          statName: choice.skillCheck.stat.charAt(0).toUpperCase() + choice.skillCheck.stat.slice(1),
          threshold: choice.skillCheck.difficulty,
          playerValue: result.playerValue,
        });
      }

      storyEngine.advanceToNode(nextNodeId);
    } else if (choice.nextNodeId) {
      storyEngine.selectChoice(choice.id);
    } else {
      storyEngine.selectChoice(choice.id);
    }
  }

  // ─── Chapter Transition ──────────────────────────────────

  private async showChapterTransition(engine: GameEngine, node: StoryNode): Promise<void> {
    const ctx = engine.getContext();
    const { frameRenderer, effects, settings } = ctx;
    const speed = settings.getTypewriterSpeed();

    // Hide sprites
    ctx.sprites.hide();

    // Draw a cinematic transition frame
    const layout = frameRenderer.drawFrame({
      title: node.act ? `ACT ${node.act}` : '',
      borderColor: 'runeGold',
      headerColor: 'runeGold',
    });

    const writer = frameRenderer.createWriter(layout.content, effects);

    // Centered chapter title card
    const offset = writer.calculateVerticalOffset(5);
    writer.skip(offset);
    writer.blankLine();

    if (node.act) {
      writer.writeCentered(`Act ${node.act}`, 'frostSilver');
      writer.blankLine();
    }

    writer.writeCentered('══════════════════════════════════════', 'runeGold');
    writer.blankLine();

    const chapterTitle = `Chapter ${node.chapter}: ${node.title}`;
    if (speed > 0) {
      await writer.typewriterLine(chapterTitle, 'icyWhite', speed);
    } else {
      writer.writeCentered(chapterTitle, 'icyWhite');
    }

    writer.blankLine();
    writer.writeCentered('══════════════════════════════════════', 'runeGold');

    await writer.pressAnyKey();
  }

  // ─── Effect Feedback ─────────────────────────────────────

  /** Flush buffered feedback messages into a PanelWriter. */
  private flushFeedback(writer: PanelWriter): void {
    if (this.feedbackBuffer.length === 0) return;
    writer.writeLine('────────────────────────────────', 'frostSilver');
    for (const msg of this.feedbackBuffer) {
      writer.writeLine(msg.text, msg.color);
    }
    writer.writeLine('────────────────────────────────', 'frostSilver');
    writer.blankLine();
    this.feedbackBuffer = [];
  }

  private setupEffectListeners(engine: GameEngine, _state: GameState): () => void {
    const { events } = engine.getContext();
    const handlers: Array<{ event: string; handler: (data: unknown) => void }> = [];
    const buf = this.feedbackBuffer;

    const listen = (event: string, handler: (data: any) => void) => {
      events.on(event, handler);
      handlers.push({ event, handler });
    };

    listen('xpChanged', (data: { xp: number; change: number }) => {
      if (data.change > 0) {
        buf.push({ text: `  ${theme.getSymbolPrefix('green')}+${data.change} XP`, color: 'green' });
      }
    });

    listen('goldChanged', (data: { gold: number; change: number }) => {
      if (data.change > 0) {
        buf.push({ text: `  ${theme.getSymbolPrefix('yellow')}+${data.change} Gold`, color: 'yellow' });
      } else if (data.change < 0) {
        buf.push({ text: `  ${theme.getSymbolPrefix('red')}${data.change} Gold`, color: 'red' });
      }
    });

    listen('itemAdded', (data: { id: string }) => {
      buf.push({ text: `  ${theme.getSymbolPrefix('magenta')}New Item: ${data.id}`, color: 'magenta' });
    });

    listen('itemRemoved', (data: { id: string }) => {
      buf.push({ text: `  ${theme.getSymbolPrefix('red')}Lost Item: ${data.id}`, color: 'red' });
    });

    listen('traitAcquired', (data: { key: string; trait: { name: string } }) => {
      buf.push({ text: `  ${theme.getSymbolPrefix('frostBlue')}+1 ${data.trait.name}`, color: 'frostBlue' });
    });

    listen('traitChanged', (data: { key: string; trait: { name: string }; change: number }) => {
      const sign = data.change > 0 ? '+' : '';
      buf.push({ text: `  ${theme.getSymbolPrefix('frostBlue')}${sign}${data.change} ${data.trait.name}`, color: 'frostBlue' });
    });

    listen('relationshipChanged', (data: { npc: string; change: number }) => {
      const sign = data.change > 0 ? '+' : '';
      buf.push({ text: `  Relationship: ${data.npc} ${sign}${data.change}`, color: 'blue' });
    });

    listen('levelUp', (data: { level: number; hpGain: number; unspentPoints: number }) => {
      buf.push({ text: `  ${theme.getSymbolPrefix('yellow')}*** LEVEL UP! ***`, color: 'yellow' });
      buf.push({ text: `  You are now level ${data.level}!`, color: 'brightWhite' });
      buf.push({ text: `  ${theme.getSymbolPrefix('green')}+${data.hpGain} HP`, color: 'green' });
      buf.push({ text: `  ${theme.getSymbolPrefix('frostBlue')}${data.unspentPoints} stat points to allocate`, color: 'frostBlue' });
    });

    listen('questUpdated', (data: { id: string; status: string }) => {
      const color = data.status === 'completed' ? 'green' : data.status === 'failed' ? 'red' : 'yellow';
      buf.push({ text: `  ${theme.getSymbolPrefix(color)}Quest: ${data.id} — ${data.status}`, color });
    });

    listen('abilityEvolved', (data: { abilityName: string; tierName: string; toTier: number }) => {
      buf.push({ text: `  ${theme.getSymbolPrefix('magenta')}*** ABILITY EVOLVED! ***`, color: 'magenta' });
      buf.push({ text: `  ${data.abilityName} → ${data.tierName} (Tier ${data.toTier})`, color: 'brightWhite' });
    });

    listen('achievementUnlocked', (data: { name: string; points: number }) => {
      buf.push({ text: `  ${theme.getSymbolPrefix('yellow')}*** ACHIEVEMENT UNLOCKED ***`, color: 'yellow' });
      buf.push({ text: `  ${data.name} (+${data.points} pts)`, color: 'brightWhite' });
    });

    return () => {
      for (const { event, handler } of handlers) {
        events.off(event, handler);
      }
    };
  }

  // ─── Visual Updates ────────────────────────────────────

  private updateVisuals(engine: GameEngine, node: StoryNode): void {
    const { visualLayer } = engine.getContext();

    // Update particle preset based on location or context
    const locationId = (node.location ?? '').toLowerCase().replace(/\s+/g, '_');
    if (locationId) {
      visualLayer.setLocationParticles(locationId);
    } else {
      // Fall back to context tag based selection
      const preset = this.selectParticlePreset(node);
      const particles = engine.getContext().particles;
      if (particles.isEnabled() && preset !== particles.getPreset()) {
        particles.setPreset(preset);
      }
    }
  }

  private selectParticlePreset(node: StoryNode): 'snow' | 'embers' | 'arcane' | 'mist' {
    const tags = node.contextTags ?? [];
    const loc = (node.location ?? '').toLowerCase();

    if (tags.includes('combat') || tags.includes('fire') || tags.includes('battle')) {
      return 'embers';
    }
    if (tags.includes('magic') || tags.includes('arcane') || tags.includes('ritual') ||
        tags.includes('temple') || loc.includes('temple') || loc.includes('sanctum')) {
      return 'arcane';
    }
    if (tags.includes('underground') || tags.includes('cave') || tags.includes('tomb') ||
        tags.includes('mysterious') || loc.includes('tomb') || loc.includes('crypt') ||
        loc.includes('shadow') || loc.includes('dungeon')) {
      return 'mist';
    }
    return 'snow';
  }

  // ─── Hint Triggers ──────────────────────────────────────

  private async triggerContextHints(
    hintManager: import('@ui/HintManager').HintManager,
    node: StoryNode,
    writer: import('@ui/PanelWriter').PanelWriter,
  ): Promise<void> {
    const tags = node.contextTags ?? [];

    if (tags.includes('skill_check')) {
      await hintManager.maybeShowHint('first_skill_check' as HintTriggerType, writer);
    }
    if (tags.includes('ability') || tags.includes('class_ability')) {
      await hintManager.maybeShowHint('first_class_ability' as HintTriggerType, writer);
    }
    if (tags.includes('faction')) {
      await hintManager.maybeShowHint('first_faction' as HintTriggerType, writer);
    }
    if (tags.includes('achievement')) {
      await hintManager.maybeShowHint('first_achievement' as HintTriggerType, writer);
    }
  }

  // ─── Ability & Achievement Checks ───────────────────────

  private checkAbilityEvolutions(engine: GameEngine, state: GameState): void {
    const { abilitySystem } = engine.getContext();
    abilitySystem.checkEvolutions(state.character.classId, state);
  }

  private checkAchievements(engine: GameEngine, state: GameState): void {
    const { achievementSystem, saveSystem } = engine.getContext();
    const vault = saveSystem.loadVault();
    const unlocked = achievementSystem.checkAll(state, vault);
    if (unlocked.length > 0) {
      saveSystem.saveVault(vault);
    }
  }

  // ─── Combat Encounter ──────────────────────────────────

  private handleCombatEncounter(
    engine: GameEngine,
    node: StoryNode,
    _state: GameState,
  ): Promise<CombatResult | null> {
    return new Promise((resolve) => {
      const enemyDef = getEnemy(node.combatEncounter!.enemyId);
      if (!enemyDef) {
        resolve(null);
        return;
      }

      const combatScene = new CombatScene(enemyDef, (result) => {
        resolve(result);
      });

      engine.pushScene(combatScene).catch((err) => {
        console.error('Failed to push CombatScene:', err);
        resolve(null);
      });
    });
  }

  // ─── Auto-Save ───────────────────────────────────────────

  private autoSave(engine: GameEngine, state: GameState, node: StoryNode): void {
    const { saveSystem } = engine.getContext();
    const cls = getClass(state.character.classId);
    saveSystem.save(0, state, node.title, cls.name);
  }

  // ─── Location Sprite Mapping ─────────────────────────────

  private getLocationSprite(location: string): string | null {
    // Map story location IDs to sprite keys
    const mapping: Record<string, string> = {
      'gallowcrest': 'gallowcrest_citadel',
      'frozen-wastes': 'frozen_wastes',
      'the-archive': 'ancient_library',
      'ancient-temple': 'temple_of_dawn',
      'ancient-temple-ruins': 'temple_of_dawn',
      'blackfrost-keep': 'shadow_keep',
      'castle-greymarch': 'gallowcrest_citadel',
      'dragon-peak': 'dragons_lair',
      'dragons-grave': 'dragons_lair',
      'crystalline-spire-academy': 'crystal_caves',
      'thornhearth': 'the_forge',
    };

    return mapping[location] ?? null;
  }
}
